import type { AssistantMode } from "../types";

export const MODE_LABEL: Record<AssistantMode, string> = {
  business: "Negócio",
  technical: "Técnico",
  support: "Suporte",
  executive: "Executivo",
  teaching: "Didático",
  concise: "Resumido",
  structured: "Estruturado",
};

interface Props {
  /** Valor atual. "" significa "herdar" (usar o nível acima). */
  value: AssistantMode | "";
  onChange: (value: AssistantMode | "") => void;
  disabled?: boolean;
  /**
   * Rótulo da opção "herdar" (value ""). Se ausente, não há opção de herdar
   * (ex.: o seletor global, que sempre tem um modo concreto).
   */
  inheritLabel?: string;
  title?: string;
  /** Rótulo curto exibido antes do select (ex.: "Modo", "Tom"). */
  caption?: string;
  /** Modos que a pessoa tem permissão de usar (controlado pelo admin). Default: os dois. */
  allowed?: AssistantMode[];
}

/**
 * Seletor de modo de resposta (Negócio / Técnico). Reutilizado em três níveis:
 * global (padrão do app), por conversa e por mensagem. Nos níveis que herdam,
 * a opção "" reaproveita o modo do nível acima. Só mostra as opções que a
 * pessoa tem permissão de usar (`allowed`).
 */
export function ModeSelector({ value, onChange, disabled, inheritLabel, title, caption, allowed }: Props) {
  const modes: AssistantMode[] = allowed?.length ? allowed : ["business", "technical", "support", "executive", "teaching", "concise", "structured"];
  return (
    <label className={`mode-selector mode-${value || "inherit"}`} title={title}>
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="2" y="4.5" width="12" height="8.5" rx="1.5" />
        <path d="M5.5 4.5V3.2a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v1.3" />
      </svg>
      {caption && <span className="mode-caption">{caption}</span>}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as AssistantMode | "")}
        disabled={disabled || modes.length <= 1}
        aria-label={caption ?? "Modo de resposta"}
      >
        {inheritLabel && <option value="">{inheritLabel}</option>}
        {modes.map((m) => (
          <option key={m} value={m}>
            {MODE_LABEL[m]}
          </option>
        ))}
      </select>
    </label>
  );
}
